import { useLayoutEffect, type ReactNode } from 'react';
import { createBrowserRouter, RouterProvider, useLocation } from 'react-router';
import BrandProvider from './store/BrandProvider';
import Layout from './components/layout/Layout';
import Page404 from './pages/404.js';

// Supports weights 300-800
import '@fontsource-variable/open-sans/wdth.css';
import '@fontsource-variable/open-sans/wdth-italic.css';
import '@fontsource-variable/material-symbols-outlined/full.css';

import './scss/bootstrap.scss';
import './scss/app.scss';

function ScrollToTop({ children }: { children: ReactNode }) {
  const location = useLocation();
  useLayoutEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  }, [location.pathname]);

  return <>{children}</>;
}

const pages = import.meta.glob('./pages/**/*.(tsx|jsx)', { eager: true });
const routes = [];

for (const path of Object.keys(pages)) {
  const fileName = path.match(/\.\/pages\/(.*)\.(tsx|jsx)$/)?.[1];
  if (!fileName) {
    continue;
  }

  const normalizedPathName = fileName
    .replace(/\[\.{3}(.+)\]/, '*') // [...wildcard] => *
    .replace(/\[(\w+)\]/g, ':$1') // [param] => :param
    .replace(/\/index$/, ''); // /index => (root of folder)

  const pageModule = pages[path] as { default: React.ComponentType<unknown>; ErrorBoundary?: React.ComponentType<unknown> };

  routes.push({
    path: fileName === 'index' ? '/' : `/${normalizedPathName.toLowerCase()}`,
    Element: pageModule.default,
    // loader: pages[path]?.loader | undefined,
    // action: pages[path]?.action | undefined,
    ErrorBoundary: pageModule.ErrorBoundary,
  });
}

const router = createBrowserRouter([
  {
    // parent route component
    element: (
      <ScrollToTop>
        <Layout />
      </ScrollToTop>
    ),
    // 404 page
    errorElement: (
      <Layout>
        <Page404 />
      </Layout>
    ),
    // child route components
    children: [
      ...routes.map(({ Element, ErrorBoundary, ...rest }) => ({
        ...rest,
        element: <Element />,
        ...(ErrorBoundary && { errorElement: <ErrorBoundary /> }),
      })),
    ],
  },
]);

const App = () => {
  return (
    <BrandProvider>
      <RouterProvider router={router} />
    </BrandProvider>
  );
};

export default App;
